/**
 * DS Coder — AI-assisted rule authoring.
 *
 * Asks the model to draft a rule body (markdown) from a name/description, then
 * validates it with zod before returning. The result is a *draft* for the user
 * to review and save — the AI never writes to the store directly.
 */
import { z } from "zod";
import { aiService } from "@/services/aiService";

const DraftSchema = z.object({
  description: z.string().trim().optional(),
  content: z.string().trim().min(1),
  globs: z.string().trim().optional(),
  alwaysApply: z.boolean().optional(),
});

export interface RuleDraft {
  description?: string;
  content: string;
  globs?: string;
  alwaysApply?: boolean;
}

function extractJson(text: string): string {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fenced ? fenced[1] : text).trim();
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start !== -1 && end !== -1 && end > start) return candidate.slice(start, end + 1);
  return candidate;
}

function buildPrompt(params: {
  name: string;
  description?: string;
  existingContent?: string;
  instruction?: string;
}): string {
  const { name, description, existingContent, instruction } = params;
  const existingBlock = existingContent?.trim()
    ? `\nHere is the EXISTING rule body — improve and expand it (keep the good parts):\n${existingContent.trim()}\n`
    : "";
  const descBlock = description?.trim() ? `\nWhat the rule is for: ${description.trim()}\n` : "";
  const extra = instruction?.trim() ? `\nAdditional guidance: ${instruction.trim()}\n` : "";

  return `You are authoring a coding "rule" (like a Cursor project rule) named "${name}" that guides an AI coding assistant while it works on an attached GitHub repo.

Produce a JSON object with EXACTLY these keys:
- "content": string — the rule body in concise markdown. Actionable, specific instructions the assistant MUST follow (bullet points and short code examples welcome).
- "description": string — one short sentence describing what the rule enforces.
- "globs"?: string — comma-separated file globs this rule should target (e.g. "**/*.ts, **/*.tsx"), or omit for a general rule.
- "alwaysApply"?: boolean — true if the rule should apply to every file regardless of globs.

Rules:
- Be specific and practical, not generic filler.
- Prefer modern, current best practices.
- Output ONLY the JSON object. No prose, no markdown fences.${descBlock}${existingBlock}${extra}`;
}

export async function generateRuleDraft(params: {
  name: string;
  description?: string;
  existingContent?: string;
  instruction?: string;
}): Promise<RuleDraft> {
  const prompt = buildPrompt(params);
  const raw = await aiService.complete(prompt, {
    model: "openai/gpt-4o",
    maxTokens: 1200,
    temperature: 0.4,
    app: "other",
  });
  if (!raw?.trim()) throw new Error("The AI returned an empty response. Try again.");

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(extractJson(raw));
  } catch {
    throw new Error("The AI response was not valid JSON. Try again.");
  }

  const result = DraftSchema.safeParse(parsedJson);
  if (!result.success) {
    throw new Error(`AI draft failed validation: ${result.error.issues[0]?.message ?? "bad shape"}`);
  }

  return {
    description: result.data.description,
    content: result.data.content,
    globs: result.data.globs,
    alwaysApply: result.data.alwaysApply,
  };
}
